"use client";

import React, { useState, useMemo } from "react";
import { Alert, Badge, Button, Card, Col, Form, InputGroup, ListGroup } from "react-bootstrap";
import Icon from "../Icon";
import { TaskList as TaskListEntity } from "../../state/TaskList";
import { Task } from "../../state/Task";
import { useData } from "../../storage/accessors";
import { AddTaskToListModal } from "../AddTaskToListModal";
import { transparentize, deadlineColor } from "../../state/helpers";
import { format, parse } from "date-fns";
import { ru } from "date-fns/locale";
import {
    faTrash as trashBin,
    faBars as bars,
    faCheck as checkmark,
    faArrowRotateLeft as refresh,
    faPlus as add,
    faEdit as pencil
} from '@fortawesome/free-solid-svg-icons';

// Компонент карточки задачи в списке
export const TaskCard: React.FC<{
    task: Task;
    taskListUuid: string;
    taskIndex: number;
    onRemove: () => void;
    onMove?: () => void;
    onReorder?: (taskUuid: string, targetIndex: number) => void;
    isMobile: boolean;
    allTasks: Task[];
    onOpenTask?: () => void;
}> = ({ task, taskListUuid, taskIndex, onRemove, onMove, onReorder, isMobile, allTasks, onOpenTask }) => {
    const { tasks, roles, plans } = useData();
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const today = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);

    const nearestPlanDate = useMemo(() => {
        const taskPlans = Object.values(plans.objects)
            .filter(p => p.task === task.uuid && p.date >= today)
            .sort((a, b) => a.date.localeCompare(b.date));
        return taskPlans.length > 0 ? taskPlans[0].date : null;
    }, [task.uuid, plans.objects, today]);

    const handleToggleDone = () => {
        tasks.save(task.uuid, { isDone: !task.isDone });
    };

    const handleOpenTask = () => {
        if (onOpenTask) {
            onOpenTask();
        }
    };

    const handleDragStart = (e: React.DragEvent) => {
        if (isMobile) {
            e.preventDefault();
            return;
        }
        e.dataTransfer.setData('task', task.uuid);
        e.dataTransfer.setData('sourceList', taskListUuid);
        e.dataTransfer.setData('dragType', 'reorder');
        e.dataTransfer.setData('taskIndex', taskIndex.toString());
        e.dataTransfer.effectAllowed = 'move';
        (e.target as HTMLElement).style.opacity = '0.5';
    };

    const handleDragEnd = (e: React.DragEvent) => {
        (e.target as HTMLElement).style.opacity = '1';
        setDragOverIndex(null);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        if (isMobile) return;
        // types доступен в dragover, в отличие от getData
        if (e.dataTransfer.types.includes('task')) {
            e.preventDefault();
            setDragOverIndex(index);
        }
    };

    const handleDrop = (e: React.DragEvent, targetIndex: number) => {
        if (isMobile) return;

        const taskUuid = e.dataTransfer.getData('task');
        const sourceList = e.dataTransfer.getData('sourceList');

        if (!taskUuid) {
            setDragOverIndex(null);
            return;
        }

        e.preventDefault();

        if (sourceList === taskListUuid && onReorder) {
            // Reorder внутри того же списка
            e.stopPropagation();
            onReorder(taskUuid, targetIndex);
        } else if (sourceList !== taskListUuid && onReorder) {
            // Перемещение из другого списка на конкретную позицию
            e.stopPropagation();
            onReorder(taskUuid, targetIndex);
        }

        setDragOverIndex(null);
    };

    // Находим самую верхнюю родительскую задачу
    const topParentTask = useMemo(() => {
        let currentTask = task;
        let topParent = null;

        while (currentTask && currentTask.parentTask) {
            const parentTask = tasks.objects[currentTask.parentTask];
            if (parentTask) {
                topParent = parentTask;
                currentTask = parentTask;
            } else {
                break;
            }
        }
        return topParent;
    }, [task.uuid, tasks.objects]);

    const part = roles.objects[task.lifepart];
    const partColor = part?.color || "#FFFFFF";
    const isDragOver = dragOverIndex === taskIndex;

    // Определяем цвет дедлайна
    const deadlineBadge = task.deadline && !task.isDone ? deadlineColor(task.deadline) : "";

    return (
        <>
            <li
                className="list-group-item ps-0 pe-0 pb-0 mb-2"
                style={{ borderBottom: 0 }}
            >
                <Card
                    draggable={!isMobile}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, taskIndex)}
                    onDrop={(e) => handleDrop(e, taskIndex)}
                    style={{
                        transform: isDragOver ? 'translateY(-2px)' : undefined,
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    }}
                >
                    <Card.Header className="pb-2 pt-3"
                        style={{
                            backgroundColor: transparentize(partColor, 0.85),
                            // padding: '0.4rem 0.6rem',
                            // borderRadius: '0.25rem',
                            cursor: 'pointer',
                            // fontSize: '0.95rem',
                            // marginBottom: '0.5rem'
                        }}

                    >
                        <Card.Title
                            style={{
                            }}
                            className="d-flex justify-content-between align-items-start"
                            onClick={handleOpenTask}
                        >
                            <div className="d-flex flex-column gap-1" style={{ flex: 1 }}>
                                <span style={{
                                    fontSize: '0.95rem',
                                    textDecoration: task.isDone ? 'line-through' : 'none',
                                    color: task.isDone ? '#6c757d' : 'inherit'
                                }}>
                                    {task.description}
                                </span>
                                <div className="d-flex flex-wrap gap-1">
                                    {topParentTask && (
                                        <Badge bg="secondary" style={{ cursor: "pointer", maxWidth: 200, display: 'inline-block' }} onClick={() => onOpenTask && onOpenTask()}>
                                            <small style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{topParentTask.description}</small>
                                        </Badge>
                                    )}
                                    {task.deadline && !task.isDone && (
                                        <Badge bg={deadlineBadge}>
                                            <small>{format(parse(task.deadline, "yyyy-MM-dd", new Date()), "dd.MM", { locale: ru })}</small>
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </Card.Title>
                    </Card.Header>
                    <ListGroup variant="flush">
                        <ListGroup.Item>
                            <div className="d-flex justify-content-between align-items-center gap-1">
                                {nearestPlanDate && (
                                    <Badge bg="light" style={{ border: '1px solid #dee2e6', color: '#212529' }}>
                                        <small>📅 {format(parse(nearestPlanDate, "yyyy-MM-dd", new Date()), "dd.MM", { locale: ru })}</small>
                                    </Badge>
                                )}
                                <div className="d-flex justify-content-end gap-1">
                                <Button
                                    title={task.isDone ? "Возобновить" : "Завершить"}
                                    size="sm"
                                    variant={task.isDone ? "outline-warning" : "outline-success"}
                                    onClick={handleToggleDone}
                                >
                                    <Icon icon={task.isDone ? refresh : checkmark} />
                                </Button>
                                {isMobile && onMove && (
                                    <Button
                                        title="Переместить"
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={onMove}
                                    >
                                        <Icon icon={bars} />
                                    </Button>
                                )}
                                <Button
                                    title="Удалить из списка"
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={onRemove}
                                >
                                    <Icon icon={trashBin} />
                                </Button>
                            </div>
                            </div>
                        </ListGroup.Item>
                    </ListGroup>
                </Card>
            </li>
        </>
    );
};

// Компонент списка задач (колонка)
export const TaskListColumn: React.FC<{
    taskList: TaskListEntity;
    tasks: Task[];
    onRemoveTask: (taskUuid: string) => void;
    onMoveTask?: (taskUuid: string) => void;
    onDropTask?: (taskUuid: string, sourceListUuid: string, targetListUuid: string) => void;
    onReorderTasks?: (taskUuid: string, targetIndex: number, listUuid: string) => void;
    onEditList?: (taskList: TaskListEntity) => void;
    onOpenTask?: (taskUuid: string) => void;
    isMobile: boolean;
    isActive?: boolean;
    onDragStart?: (e: React.DragEvent) => void;
    onDragOver?: (e: React.DragEvent) => void;
    onDrop?: (e: React.DragEvent) => void;
    onDragEnd?: (e: React.DragEvent) => void;
}> = ({
    taskList,
    tasks,
    onRemoveTask,
    onMoveTask,
    onDropTask,
    onReorderTasks,
    onEditList,
    onOpenTask,
    isMobile,
    isActive,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
}) => {
        const [dragOver, setDragOver] = useState(false);
        const [showAddTask, setShowAddTask] = useState(false);
        const [showAddTaskStart, setShowAddTaskStart] = useState(false);
        const [searchTerm, setSearchTerm] = useState('');
        const [showCompleted, setShowCompleted] = useState(false);

        const handleDragOver = (e: React.DragEvent) => {
            if (isMobile) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setDragOver(true);
        };

        const handleDragLeave = () => {
            setDragOver(false);
        };

        const handleDrop = (e: React.DragEvent) => {
            if (isMobile) return;
            e.preventDefault();
            setDragOver(false);
            const taskUuid = e.dataTransfer.getData('task');
            const sourceListUuid = e.dataTransfer.getData('sourceList');
            if (taskUuid && sourceListUuid && onDropTask) {
                onDropTask(taskUuid, sourceListUuid, taskList.uuid);
            }
        };

        const filteredTasks = useMemo(() => {
            let result = tasks;

            // Фильтр по завершенным задачам
            if (!showCompleted) {
                result = result.filter(t => !t.isDone);
            }

            // Фильтр по поиску
            if (searchTerm) {
                result = result.filter(t =>
                    (t.description || "").toLowerCase().includes(searchTerm.toLowerCase())
                );
            }

            return result;
        }, [tasks, searchTerm, showCompleted]);

        return (
            <Col
                style={{
                    minWidth: isMobile ? '100%' : '330px',
                    maxWidth: isMobile ? '100%' : '400px',
                    opacity: dragOver ? 0.8 : 1,
                    transition: 'opacity 0.2s ease',
                    backgroundColor: dragOver ? 'rgba(102, 126, 234, 0.15)' : 'transparent',
                    borderRadius: '8px',
                    display: isMobile && !isActive ? 'none' : 'flex',
                    flexDirection: 'column'
                }}
                onDragStart={(e) => {
                    // Если это drag заголовка списка (не карточки), даём событию распространиться
                    const target = e.target as HTMLElement;
                    if (target.closest('[data-list-header]')) {
                        if (onDragStart) onDragStart(e);
                    } else {
                        e.stopPropagation();
                    }
                }}
                onDragOver={(e) => {
                    const dragType = e.dataTransfer.getData('dragType');
                    const sourceList = e.dataTransfer.getData('sourceList');

                    // Если это reorder внутри того же списка — пропускаем событие к карточкам
                    if (dragType === 'reorder' && sourceList === taskList.uuid) {
                        return;
                    }

                    // Карточка из другого списка или плана — обрабатываем на уровне колонки
                    e.preventDefault();
                    if (onDragOver) {
                        onDragOver(e);
                    }
                }}
                onDragLeave={handleDragLeave}
                onDrop={(e) => {
                    const taskUuid = e.dataTransfer.getData('task');
                    const sourceList = e.dataTransfer.getData('sourceList');

                    if (!taskUuid) return;

                    // Если это reorder внутри того же списка — не обрабатываем на колонке
                    if (sourceList === taskList.uuid) {
                        return;
                    }

                    e.preventDefault();
                    e.stopPropagation();
                    // Дроп на пустую область колонки — перемещаем в конец списка
                    if (onDropTask) {
                        onDropTask(taskUuid, sourceList, taskList.uuid);
                    }
                }}
            >
                <Card className="mb-3" style={{ borderLeft: `4px solid ${taskList.color}` }}>
                    <Card.Header
                        data-list-header
                        style={{ backgroundColor: taskList.color, color: '#fff', cursor: !isMobile ? 'grab' : 'default' }}
                        draggable={!isMobile}
                        onDragStart={(e) => {
                            e.stopPropagation();
                            if (onDragStart) onDragStart(e);
                        }}
                        onDragOver={(e) => {
                            e.stopPropagation();
                            if (onDragOver) onDragOver(e);
                        }}
                        onDrop={(e) => {
                            e.stopPropagation();
                            if (onDrop) onDrop(e);
                        }}
                        onDragEnd={onDragEnd}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <div className="d-flex align-items-center gap-2">
                                <h5 className="mb-0">{taskList.name}</h5>
                                <Button
                                    size="sm"
                                    variant="link"
                                    className="p-0 text-light"
                                    onClick={() => setShowAddTaskStart(true)}
                                    title="Добавить задачу в начало списка"
                                >
                                    <Icon icon={add} size="sm" />
                                </Button>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <Badge bg="secondary">{filteredTasks.length}</Badge>
                                {onEditList && (
                                    <Button
                                        size="sm"
                                        variant="link"
                                        className="p-0 text-light"
                                        onClick={() => onEditList(taskList)}
                                        title="Настроить список"
                                    >
                                        <Icon icon={pencil} size="sm" />
                                    </Button>
                                )}
                            </div>
                        </div>
                        <div className="d-flex align-items-center">
                            <Form.Check
                                type="switch"
                                id={`show-completed-${taskList.uuid}`}
                                label="Завершённые"
                                checked={showCompleted}
                                onChange={(e) => setShowCompleted(e.target.checked)}
                                className="mb-0"
                                style={{ fontSize: '0.85rem' }}
                            />
                        </div>
                    </Card.Header>
                    <Card.Body className="p-2" style={{ overflowY: 'visible', flex: 1 }}>
                        {tasks.length > 5 && (
                            <InputGroup className="mb-2">
                                <Form.Control
                                    type="text"
                                    placeholder="Поиск задач..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setSearchTerm('')}
                                        title="Очистить поиск"
                                    >
                                        <Icon icon={trashBin} size="sm" />
                                    </Button>
                                )}
                            </InputGroup>
                        )}
                        <ul className="list-group list-group-flush" style={{ minHeight: '100px' }}>
                            {filteredTasks.map((task, index) => {
                                // Индекс в полном (неотфильтрованном) списке для корректного reorder
                                const fullIndex = tasks.findIndex(t => t.uuid === task.uuid);
                                return (
                                    <TaskCard
                                        key={task.uuid}
                                        task={task}
                                        taskListUuid={taskList.uuid}
                                        taskIndex={fullIndex}
                                        allTasks={filteredTasks}
                                        onRemove={() => onRemoveTask(task.uuid)}
                                        onMove={onMoveTask ? () => onMoveTask(task.uuid) : undefined}
                                        onReorder={onReorderTasks ? (taskUuid, targetIndex) => onReorderTasks(taskUuid, targetIndex, taskList.uuid) : undefined}
                                        isMobile={isMobile}
                                        onOpenTask={onOpenTask ? () => onOpenTask(task.uuid) : undefined}
                                    />
                                );
                            })}
                            {filteredTasks.length === 0 && tasks.length > 0 && (
                                <Alert variant="info" className="mb-0 py-2">
                                    Нет задач по поиску
                                </Alert>
                            )}
                            {filteredTasks.length === 0 && tasks.length === 0 && (
                                <div className="text-center text-muted py-3">
                                    <small>Список пуст</small>
                                </div>
                            )}
                        </ul>
                        <div className="mt-2 text-center">
                            <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => setShowAddTask(true)}
                            >
                                <Icon icon={add} /> Добавить задачу
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
                <AddTaskToListModal
                    show={showAddTaskStart}
                    onHide={() => setShowAddTaskStart(false)}
                    taskListUuid={taskList.uuid}
                    addToStart={true}
                />
                <AddTaskToListModal
                    show={showAddTask}
                    onHide={() => setShowAddTask(false)}
                    taskListUuid={taskList.uuid}
                    addToStart={false}
                />
            </Col>
        );
    };

// Компонент карточки для незапланированных задач (переиспользует стили TaskCard)
export const UnplannedTaskCard: React.FC<{
    task: Task;
    onOpenTask: () => void;
    onToggleDone: () => void;
    onDelete: () => void;
    onDragStart?: (e: React.DragEvent) => void;
    onDragEnd?: (e: React.DragEvent) => void;
    isMobile: boolean;
}> = ({ task, onOpenTask, onToggleDone, onDelete, onDragStart, onDragEnd, isMobile }) => {
    const { roles, plans } = useData();
    const today = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);

    const nearestPlanDate = useMemo(() => {
        const taskPlans = Object.values(plans.objects)
            .filter(p => p.task === task.uuid && p.date >= today)
            .sort((a, b) => a.date.localeCompare(b.date));
        return taskPlans.length > 0 ? taskPlans[0].date : null;
    }, [task.uuid, plans.objects, today]);

    const part = roles.objects[task.lifepart];
    const partColor = part?.color || "#FFFFFF";

    // Определяем цвет дедлайна
    const deadlineBadge = task.deadline && !task.isDone ? deadlineColor(task.deadline) : "";

    return (
        <li
            className="list-group-item ps-0 pe-0 pb-0 mb-2"
            style={{ borderBottom: 0 }}
        >
            <Card
                draggable={!isMobile}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
            >
                <Card.Header className="pb-2 pt-3"
                    style={{
                        backgroundColor: transparentize(partColor, 0.85),
                        cursor: 'pointer',
                    }}
                    onClick={onOpenTask}
                >
                    <Card.Title
                        className="d-flex justify-content-between align-items-start"
                    >
                        <div className="d-flex flex-column gap-1" style={{ flex: 1 }}>
                            <span style={{
                                fontSize: '0.95rem',
                                textDecoration: task.isDone ? 'line-through' : 'none',
                                color: task.isDone ? '#6c757d' : 'inherit'
                            }}>
                                {task.description}
                            </span>
                            <div className="d-flex flex-wrap gap-1">
                                {task.deadline && !task.isDone && (
                                    <Badge bg={deadlineBadge}>
                                        <small>{format(parse(task.deadline, "yyyy-MM-dd", new Date()), "dd.MM", { locale: ru })}</small>
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </Card.Title>
                </Card.Header>
                <ListGroup variant="flush">
                    <ListGroup.Item>
                        <div className="d-flex justify-content-between align-items-center gap-1">
                            {nearestPlanDate && (
                                <Badge bg="light" style={{ border: '1px solid #dee2e6', color: '#212529' }}>
                                    <small>📅 {format(parse(nearestPlanDate, "yyyy-MM-dd", new Date()), "dd.MM", { locale: ru })}</small>
                                </Badge>
                            )}
                            <div className="d-flex justify-content-end gap-1">
                            <Button
                                title={task.isDone ? "Возобновить" : "Завершить"}
                                size="sm"
                                variant={task.isDone ? "outline-warning" : "outline-success"}
                                onClick={onToggleDone}
                            >
                                <Icon icon={task.isDone ? refresh : checkmark} />
                            </Button>
                            <Button
                                title="Удалить задачу"
                                size="sm"
                                variant="outline-danger"
                                onClick={onDelete}
                            >
                                <Icon icon={trashBin} />
                            </Button>
                            </div>
                            </div>
                        </ListGroup.Item>
                </ListGroup>
            </Card>
        </li>
    );
};
